import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils/cn";
import { fuzzyRank } from "@/lib/utils/fuzzy";
import type { AllowableValue } from "@/types";

interface SearchableSelectProps {
  value: string | null;
  options: AllowableValue[];
  onChange: (value: string) => void;
  invalid?: boolean;
  placeholder?: string;
}

/** Dynamic-list control: a combobox with fuzzy search for large domains. */
export function SearchableSelect({
  value,
  options,
  onChange,
  invalid,
  placeholder = "Select…",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedLabel = useMemo(
    () => options.find((o) => String(o.value) === String(value))?.label,
    [options, value],
  );

  const ranked = useMemo(
    () => fuzzyRank(query, options, (o) => o.label),
    [query, options],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-lg border bg-card/80 px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
            invalid ? "border-destructive" : "border-input",
          )}
        >
          <span className={cn(!selectedLabel && "text-muted-foreground/70")}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground/60" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-7 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1 scrollbar-thin">
          {ranked.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              No matches
            </p>
          )}
          {ranked.map(({ item }) => {
            const isSelected = String(item.value) === String(value);
            return (
              <button
                key={String(item.value)}
                type="button"
                onClick={() => {
                  onChange(String(item.value));
                  setOpen(false);
                  setQuery("");
                }}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary",
                  isSelected && "font-medium",
                )}
              >
                <span>{item.label}</span>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
